import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { easing } from "../../lib/animations";
import { MonoLabel } from "./primitives";

const pad2 = (n) => String(n).padStart(2, "0");

const computeDelta = (target) => {
    const ms = Math.max(0, new Date(target).getTime() - Date.now());
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const seconds = Math.floor((ms / 1000) % 60);
    return { ms, days, hours, minutes, seconds, done: ms === 0 };
};

/**
 * Countdown — large brutalist DD · HH · MM · SS display.
 * Each digit pair flips in via AnimatePresence on every tick.
 * Style intentionally restrained: mono-spaced, oversized, ink-on-white capable
 * via the `tone` prop ("on-ink" = white text on dark bg, "ink" = inverse).
 *
 * Interval is keyed on `target` only — completion check happens inside the
 * tick itself, avoiding stale-closure re-subscription bugs.
 */
export const Countdown = ({
    target,
    tone = "on-ink",
    size = "default",
    onComplete,
    "data-testid": testId = "countdown",
}) => {
    const [delta, setDelta] = useState(() => computeDelta(target));
    const doneRef = useRef(delta.done);
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    useEffect(() => {
        // Run the first tick immediately so the UI never sits on stale data
        // for a full second after mount.
        const tick = () => {
            const next = computeDelta(target);
            setDelta(next);
            if (next.done && !doneRef.current) {
                doneRef.current = true;
                if (onCompleteRef.current) onCompleteRef.current();
            }
        };
        doneRef.current = computeDelta(target).done;
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [target]);

    const colorPrimary = tone === "ink" ? "var(--ink)" : "var(--on-ink)";
    const colorMute = tone === "ink" ? "rgba(12,12,12,0.55)" : "var(--on-ink-mute)";
    const numFs =
        size === "lg" ? "clamp(60px, 9vw, 130px)" : "clamp(36px, 6.4vw, 84px)";

    const units = [
        { value: delta.days, label: "Days", idx: "01" },
        { value: delta.hours, label: "Hours", idx: "02" },
        { value: delta.minutes, label: "Minutes", idx: "03" },
        { value: delta.seconds, label: "Seconds", idx: "04" },
    ];

    return (
        <div data-testid={testId} className="proof-countdown">
            {units.map((u) => (
                <div
                    key={u.label}
                    data-testid={`${testId}-${u.label.toLowerCase()}`}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                        minWidth: 0,
                    }}
                >
                    <span
                        style={{
                            fontFamily: "var(--font-mono)",
                            fontWeight: 600,
                            fontSize: numFs,
                            letterSpacing: "-0.02em",
                            lineHeight: 0.95,
                            color: colorPrimary,
                            overflow: "hidden",
                            display: "inline-block",
                            position: "relative",
                            minWidth: "1.6em",
                            textAlign: "center",
                        }}
                    >
                        <AnimatePresence mode="popLayout" initial={false}>
                            <motion.span
                                key={u.value}
                                initial={{ y: "0.55em", opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: "-0.55em", opacity: 0 }}
                                transition={{ duration: 0.4, ease: easing.primary }}
                                style={{ display: "inline-block" }}
                            >
                                {pad2(u.value)}
                            </motion.span>
                        </AnimatePresence>
                    </span>
                    <MonoLabel
                        size={10}
                        style={{
                            color: colorMute,
                            letterSpacing: "0.32em",
                        }}
                    >
                        [{u.idx}] {u.label}
                    </MonoLabel>
                </div>
            ))}
        </div>
    );
};

export default Countdown;
